<template>
  <div class="form-basket">
    <form class="form-basket__contacts">
      <div class="form-basket__label">{{ $t('Контактні дані') }}</div>
      <div class="form-basket__fields">
        <BaseInput :value="inputs.name.value"
                   :error="inputs.name.error"
                   :label="$t('Ім\'я')"
                   @input="setInput(['name', $event])"
                   class="form-basket__input"
        />
        <BaseInput type="tel"
                   :value="inputs.phone.value"
                   :error="inputs.phone.error"
                   @input="setInput(['phone', $event])"
                   mask="+38 (099) 999-99-99"
                   placeholder="+38 (0__) ___-__-__"
                   :label="$t('Телефон')"
                   class="form-basket__input"
        />
      </div>
      <div class="form-basket__label">{{ $t('Спосіб доставки') }}</div>
      <div class="form-basket__delivery-method-radio-list">
        <BaseRadioList :value="inputs.delivery_type.value"
                       :error="inputs.delivery_type.error"
                       @input="setInput(['delivery_type', $event])"
                       :options="deliveryMethods"
                       :getOptionLabel="getDeliveryOptionLabel"
                       class="form-basket__delivery-method-radio"
        />
      </div>
      <transition name="fade" mode="out-in">
        <div v-if="showByDelivery([DELIVERY_METHOD_ADDRESS])"
             key="address"
             class="form-basket__places">
          <BaseSelect key="city"
                      :label="$t('Місто / населений пункт')"
                      :options="citiesList"
                      text-key="name"
                      :value="inputs.city_id.value"
                      :error="inputs.city_id.error"
                      @input="setInput(['city_id', $event])"
                      class="form-basket__select"
          />
          <BaseInput key="street"
                     :label="$t('Вулиця')"
                     :value="inputs.street.value"
                     @input="setInput(['street', $event])"
                     :error="inputs.street.error"
                     class="form-basket__input-group"
          />
          <BaseInput key="entrance"
                     :label="$t(`Під'їзд`)"
                     :value="inputs.entrance.value"
                     @input="setInput(['entrance', $event])"
                     :error="inputs.entrance.error"
                     class="form-basket__input-group"
          />
          <BaseInput key="house_number"
                     :label="$t('№ будинку')"
                     :value="inputs.house_number.value"
                     @input="setInput(['house_number', $event])"
                     :error="inputs.house_number.error"
                     class="form-basket__input-group"
          />
          <BaseInput key="apartment_number"
                     :label="$t('№ квартири')"
                     :value="inputs.apartment_number.value"
                     @input="setInput(['apartment_number', $event])"
                     :error="inputs.apartment_number.error"
                     class="form-basket__input-group"
          />
          <DatePickerInput key="time"
                           class="form-basket__input-group"
                           :value="inputs.time.value"
                           :label="$t('Час доставки')"
                           v-bind="dateOptions"
                           @input="setInput(['time', $event])"
                           :error="inputs.time.error"
                           @change="handleChangeTime"
                           :open.sync="openTime"
          />
          <BaseInput key="comment"
                     :label="$t('Побажання до замовлення')"
                     textarea
                     :value="inputs.comment.value"
                     :error="inputs.comment.error"
                     @input="setInput(['comment', $event])"
                     row="4"
                     class="form-basket__input _full-width"
          />
        </div>
        <div v-else-if="showByDelivery([DELIVERY_METHOD_SELF])"
             key="self"
             class="form-basket__places">
          <BaseSelect key="restaurant"
                      :label="$t('Оберіть заклад')"
                      :options="restaurantsSelfPickup"
                      text-key="name"
                      :value="inputs.restaurant.value"
                      :error="inputs.restaurant.error"
                      @input="setInput(['restaurant', $event])"
                      class="form-basket__input _full-width"
                      :disabled="restaurantsSelfPickup.length <= 1"
          />
          <DatePickerInput key="time"
                           class="form-basket__input _full-width"
                           :value="inputs.time_self.value"
                           :label="$t('Час приготування')"
                           v-bind="dateOptions"
                           @input="setInput(['time_self', $event])"
                           :open.sync="openTime"
                           :error="inputs.time.error || inputs.time_self.error"
                           @change="handleChangeTime"
          />
          <BaseInput key="comment"
                     :label="$t('Побажання до замовлення')"
                     textarea
                     :value="inputs.comment.value"
                     :error="inputs.comment.error"
                     @input="setInput(['comment', $event])"
                     row="4"
                     class="form-basket__input _full-width"
          />
        </div>
      </transition>

      <div class="form-basket__label">{{ $t('Cпосіб оплати') }}</div>
      <div class="form-basket__versions">
        <BaseRadioList :value="inputs.payment_type.value"
                       :error="inputs.payment_type.error"
                       @input="setInput(['payment_type', $event])"
                       :options="paymentMethods"
                       class="form-basket__radio-group"
        />
      </div>
    </form>
  </div>
</template>

<script>
import formMixins from '@/mixins/formMixins'
import {mapState} from 'vuex'
import {
  DELIVERY_METHOD_ADDRESS,
  DELIVERY_METHOD_SELF
} from '@/constants/deliveryMethods'
import DatePickerInput from '@/components/DatePickerInput'

export default {
  mixins: [formMixins],
  name: 'FormBasket',
  props: {
    inputs: {
      type: Object,
      default: () => {
      },
      required: true,
    },
    restaurantsSelfPickup: null
  },
  components: {
    DatePickerInput
  },
  data() {
    return {
      DELIVERY_METHOD_ADDRESS,
      DELIVERY_METHOD_SELF,
      openTime: false
    }
  },
  computed: {
    ...mapState('basket', ['basket']),
    ...mapState('citiesList', ['citiesList']),
    ...mapState('settings', ['settings']),
    ...mapState({
      deliveryMethods(state) {
        return state.deliveryMethods.map(item => {
          item = {...item}
          item.name = this.$t(item.name)
          return item
        })
      },
      paymentMethods(state) {
        let options = state.paymentMethods.map(item => {
          item = {...item}
          item.name = this.$t(item.name)
          return item
        })

        if (this.basket.some(item => item.product.weight_dish) || !this.settings.availableOnlinePay) {
          options = options.filter(item => item.id !== 2)
        }

        return options
      },
    }),
    getMinTimeForPreparing() {
      if (this.showByDelivery([DELIVERY_METHOD_ADDRESS])) {
        return parseFloat(this.settings && this.settings.minCookingTime || 0)
      }
      if (this.showByDelivery([DELIVERY_METHOD_SELF])) {
        return parseFloat(this.settings && this.settings.minCookingTimeSelfPickup || 0)
      }
      return 0
    },
    getMinDateTime() {
      let now = new Date()
      let minTimeDelta = this.getMinTimeForPreparing || 0
      now.setMinutes(now.getMinutes() + minTimeDelta)
      return now
    },
    timeOptions() {
      let schedule = {}
      if (this.showByDelivery([DELIVERY_METHOD_ADDRESS])) {
        schedule.start = this.settings && this.settings.delivery && this.settings.delivery.start || '00:00'
        schedule.end = this.settings && this.settings.delivery && this.settings.delivery.end || '00:00'
      }
      if (this.showByDelivery([DELIVERY_METHOD_SELF])) {
        schedule.start = this.settings && this.settings.self_picking && this.settings.self_picking.start || '00:00'
        schedule.end = this.settings && this.settings.self_picking && this.settings.self_picking.end || '00:00'
      }

      let newDate = new Date(`0000-01-01T${schedule.start}:00`)
      newDate.setMinutes(newDate.getMinutes() + this.getMinTimeForPreparing || 0)
      let minTime = `${newDate.getHours().toString().padStart(2, '0')}:${newDate.getMinutes().toString().padStart(2, '0')}`

      return {
        start: minTime,
        end: schedule.end,
        step: '00:05',
        format: 'HH:mm'
      }
    },
    dateOptions() {
      return {
        type: 'datetime',
        format: 'HH:mm',
        editable: false,
        defaultValue: this.getMinDateTime,
        disabledDate: this.disabledDate,
        disabledTime: this.disabledDateTime,
        timePickerOptions: this.timeOptions,
        clearable: false
      }
    },
    test() {
      return this.inputs && this.inputs.name && this.inputs.name.value
    }
  },
  methods: {
    showByDelivery(types) {
      const id = this.inputs.delivery_type.value && this.inputs.delivery_type.value.id
      return id && types.includes(id)
    },
    setInput(value) {
      this.$emit('input', value)
    },
    disabledDate(date) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return date < today
    },
    disabledDateTime(date) {
      return date < this.getMinDateTime
    },
    handleChangeTime(value, type) {
      if (type === 'minute') {
        this.openTime = false
      }
    },
    getDeliveryOptionLabel(option) {
      if (this.settings.isSelfPickingActionAvailable
        && this.settings.selfPickingActionDiscount > 0
        && option.id === DELIVERY_METHOD_SELF) {
        return `${option.name} <b>(-${this.settings.selfPickingActionDiscount}%)</b>`
      }
      return option.name
    }
  },
  created() {
    this.$store.dispatch('citiesList/load').then(() => {
      let defaultCity = this.citiesList.find(item => item.id == this.settings.homeCityId)
      if (defaultCity) {
        this.setInput(['city_id', defaultCity])
      } else {
        this.setInput(['city_id', this.citiesList[0]])
      }
    })
    this.setInput(['delivery_type', this.deliveryMethods[0]])
    this.setInput(['payment_type', this.paymentMethods[0]])
  },
}
</script>
